import { Link } from 'react-router-dom';
import { Button, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ACTIONS } from '../../constants/actionTables';
import { formatDate } from '../../helpers/formatDate';
import { isTaskOutdated } from '../../utils/taskUtils';
import { isTaskReadonly } from '../../utils/taskStatusUtils';

/**
 * TaskButton component - Renders different button styles based on task type and view mode
 */
export default function TaskButton({
  taskData,
  type,
  view,
  backgroundTask,
  borderColorTask,
  onOpen,
}) {
  const { t } = useTranslation();

  if (!taskData) return null;

  const readonly = isTaskReadonly(taskData);
  const outdated = isTaskOutdated(taskData.updatedAt, taskData.notifyTime);

  // Profile type button
  if (type === ACTIONS.PROFILE) {
    return (
      <Tooltip title={!outdated ? t('modals.notAvailable') : null} placement="top">
        <span>
          <Button
            variant="contained"
            color="primary"
            onClick={onOpen}
            disabled={!outdated}
            style={{
              textTransform: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-around',
              padding: '8px 15px',
              borderRadius: '4px',
              border: taskData.status === 'outdated' ? '1px dashed #B44F4F' : '1px dashed #000',
              background: taskData.status === 'outdated' ? '#F8EDED' : '#fff',
              width: '100%',
              color: 'white',
              minHeight: '56px',
            }}
          >
            <Typography
              variant="subtitle2"
              fontWeight="bold"
              style={{
                wordWrap: 'break-word',
                textAlign: 'left',
                display: '-webkit-box',
                lineHeight: '1.2em',
                maxHeight: '2.4em',
              }}
            >
              {taskData.maintenanceName}
            </Typography>
            <Typography variant="subtitle2" fontWeight="bold">
              {formatDate(taskData.createdAt)}
            </Typography>
          </Button>
        </span>
      </Tooltip>
    );
  }

  // Calendar type button with view
  if (type === ACTIONS.CALENDAR && view) {
    return (
      <Tooltip title={readonly ? t('modals.notAvailable') : null} placement="top">
        <span>
          <Button
            variant="text"
            onClick={() => {
              if (readonly) return;
              onOpen();
            }}
            sx={{
              textTransform: 'none',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              justifyContent: 'flex-start',
              padding: '0',
              borderRadius: '8px',
              border: '1px solid var(--table-border)',
              backgroundColor: 'var(--table-row-white)',
              width: '100%',
              minHeight: '60px',
              position: 'relative',
              overflow: 'hidden',
              boxShadow: 'none',
              background: 'var(--table-row-white)',
              '&:before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                width: '3px',
                height: '100%',
                background: '#2563eb',
                borderRadius: '0',
              },
              '&:hover': {
                transform: 'none',
                boxShadow: 'none',
                background: 'var(--table-hover)',
                borderColor: 'var(--border-hover)',
                '&:before': {
                  width: '3px',
                },
              },
              '&:active': {
                transform: 'none',
              },
              transition: 'all 0.2s ease',
            }}
            disableRipple={readonly}
          >
            <div
              style={{
                width: '100%',
                padding: '12px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                alignItems: 'flex-start',
                gap: '6px',
                textAlign: 'left',
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 600,
                  color: 'var(--text-heading)',
                  fontSize: '13px',
                  lineHeight: '1.2',
                  wordBreak: 'break-word',
                  textAlign: 'left',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxHeight: '2.4em',
                }}
              >
                {taskData.maintenanceName === 'Report a repair'
                  ? t('modals.reportRepair')
                  : taskData.maintenanceName}
              </Typography>
              <div>
                <div
                  style={{
                    width: '6px',
                    height: '6px',
                    borderRadius: '50%',
                    backgroundColor: '#2563eb',
                    flexShrink: 0,
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    color: 'var(--text-secondary)',
                    fontSize: '11px',
                    lineHeight: '1.2',
                    wordBreak: 'break-word',
                    textAlign: 'left',
                    fontWeight: 500,
                    display: '-webkit-box',
                    WebkitLineClamp: 1,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  <Link
                    onClick={(e) => e.stopPropagation()}
                    to={`machines/${taskData.machineId}`}
                    style={{
                      color: '#2563eb',
                      textDecoration: 'none',
                      fontWeight: '600',
                      transition: 'color 0.2s ease',
                    }}
                  >
                    {taskData.machineNumber} {taskData.machineName}
                  </Link>
                </Typography>
              </div>
            </div>
          </Button>
        </span>
      </Tooltip>
    );
  }

  // Calendar type button without view (simple view button)
  if (type === ACTIONS.CALENDAR && !view) {
    return (
      <Tooltip title={readonly ? t('modals.notAvailable') : null} placement="top">
        <span>
          <Button
            variant="outlined"
            onClick={onOpen}
            disabled={readonly}
            sx={{
              border: '1px solid rgb(25, 118, 210)',
              borderRadius: '4px',
              height: '36px',
              minWidth: '36px',
              width: '36px',
              padding: 0,
              color: 'rgb(25, 118, 210)',
              backgroundColor: 'transparent',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '&:hover': {
                border: '1px solid rgb(25, 118, 210)',
                backgroundColor: 'rgba(25, 118, 210, 0.04)',
              },
              '&:disabled': {
                border: '1px solid rgba(0, 0, 0, 0.12)',
                color: 'rgba(0, 0, 0, 0.26)',
              },
            }}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8 3C4.5 3 1.5 8 1.5 8C1.5 8 4.5 13 8 13C11.5 13 14.5 8 14.5 8C14.5 8 11.5 3 8 3Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M8 10.5C9.38071 10.5 10.5 9.38071 10.5 8C10.5 6.61929 9.38071 5.5 8 5.5C6.61929 5.5 5.5 6.61929 5.5 8C5.5 9.38071 6.61929 10.5 8 10.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </Button>
        </span>
      </Tooltip>
    );
  }

  return null;
}
